import type { ReactNode } from 'react';
import { useForm } from '@mantine/form';
import {
  TextInput, PasswordInput, Textarea, NumberInput, Switch, Select, MultiSelect, Button, Flex, Stack, Text, Box,
} from '@mantine/core';
import { trpc } from '../lib/trpc';
import type { FormField } from '../lib/types';
import FileUpload from './FileUpload';
import RichTextEditor from './RichTextEditor';
import PairsInput, { toPairs } from './PairsInput';

function LabeledField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>{label}</Text>
      {children}
    </Stack>
  );
}

interface RelationSelectProps {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  label: string;
}

function useRelationOptions(field: FormField) {
  const relation = field.relation;
  const query = trpc.admin.crud.list.useQuery(
    { entity: relation?.entity ?? '', page: 1, pageSize: 200 },
    { enabled: !!relation },
  );
  return (query.data?.rows ?? []).map((row) => ({
    value: String(row['id']),
    label: String(row[relation?.labelField ?? 'id'] ?? row['id']),
  }));
}

function RelationSelect({ field, value, onChange, required, label }: RelationSelectProps) {
  const data = useRelationOptions(field);
  return (
    <Select
      label={label}
      required={required}
      data={data}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
      clearable
      searchable
    />
  );
}

interface RelationMultiSelectProps {
  field: FormField;
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
}

function RelationMultiSelect({ field, value, onChange, label }: RelationMultiSelectProps) {
  const data = useRelationOptions(field);
  return <MultiSelect label={label} data={data} value={value} onChange={onChange} clearable searchable />;
}

function toIdStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

function buildInitialData(
  fields: FormField[],
  initialData: Record<string, unknown> | null,
): Record<string, unknown> {
  return Object.fromEntries(
    fields.map((field) => {
      const existing = initialData?.[field.key];
      if (field.type === 'pairs') return [field.key, toPairs(existing)];
      if (field.multiple) return [field.key, toIdStrings(existing)];
      if (existing !== undefined) return [field.key, existing];
      if (field.type === 'boolean') return [field.key, false];
      if (field.type === 'multiref') return [field.key, []];
      return [field.key, ''];
    }),
  );
}

function dropBlankPairs(fields: FormField[], values: Record<string, unknown>): Record<string, unknown> {
  const pairKeys = fields.filter((field) => field.type === 'pairs').map((field) => field.key);
  return {
    ...values,
    ...Object.fromEntries(
      pairKeys.map((key) => [key, toPairs(values[key]).filter((pair) => pair.value.trim() !== '' || pair.label.trim() !== '')]),
    ),
  };
}

interface EditFormProps {
  fields: FormField[];
  initialData: Record<string, unknown> | null;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export default function EditForm({ fields, initialData, onSubmit, onCancel, isSubmitting }: EditFormProps) {
  const isEditing = initialData !== null;

  const form = useForm<Record<string, unknown>>({
    initialValues: buildInitialData(fields, initialData),
    validate: Object.fromEntries(
      fields
        .filter((f) => f.required && !f.readOnly)
        .map((f) => {
          if (f.type === 'password' && isEditing) return [f.key, () => null];
          return [
            f.key,
            (value: unknown) =>
              value === '' || value === null || value === undefined ? `${f.title} is required` : null,
          ];
        }),
    ),
  });

  function renderField(field: FormField) {
    const label = field.title;
    const required = field.required;

    if (field.readOnly) {
      return (
        <LabeledField label={label}>
          <Text c="dimmed">{String(form.values[field.key] ?? '—')}</Text>
        </LabeledField>
      );
    }

    switch (field.type) {
      case 'text':
        return <TextInput label={label} required={required} {...form.getInputProps(field.key)} />;
      case 'password':
        return (
          <PasswordInput
            label={label}
            required={required && !isEditing}
            placeholder={isEditing ? 'Leave blank to keep current' : undefined}
            {...form.getInputProps(field.key)}
          />
        );
      case 'textarea':
        return <Textarea label={label} required={required} autosize minRows={4} {...form.getInputProps(field.key)} />;
      case 'number':
        return <NumberInput label={label} required={required} {...form.getInputProps(field.key)} />;
      case 'boolean':
        return <Switch label={label} {...form.getInputProps(field.key, { type: 'checkbox' })} />;
      case 'select': {
        if (field.options) {
          if (field.multiple) {
            return (
              <MultiSelect
                label={label}
                required={required}
                data={field.options.map((o) => ({ value: String(o.value), label: o.label }))}
                value={toIdStrings(form.values[field.key])}
                onChange={(v) => form.setFieldValue(field.key, v)}
                clearable
              />
            );
          }
          return (
            <Select
              label={label}
              required={required}
              data={field.options.map((o) => ({ value: String(o.value), label: o.label }))}
              value={String(form.values[field.key] ?? '') || null}
              onChange={(v) => form.setFieldValue(field.key, v ?? '')}
              clearable
            />
          );
        }
        if (field.relation) {
          return (
            <RelationSelect
              field={field}
              label={label}
              required={required}
              value={String(form.values[field.key] ?? '')}
              onChange={(v) => form.setFieldValue(field.key, v)}
            />
          );
        }
        return null;
      }
      case 'richtext':
        return (
          <LabeledField label={label}>
            <RichTextEditor
              value={String(form.values[field.key] ?? '')}
              onChange={(v) => form.setFieldValue(field.key, v)}
            />
          </LabeledField>
        );
      case 'image':
      case 'file': {
        const current = form.values[field.key];
        return (
          <LabeledField label={label}>
            <FileUpload
              value={typeof current === 'string' ? current : null}
              onChange={(v) => form.setFieldValue(field.key, v)}
              accept={field.type === 'image' ? 'image' : 'file'}
              fileTypes={field.accept}
            />
          </LabeledField>
        );
      }
      case 'multiref':
        return (
          <RelationMultiSelect
            field={field}
            label={label}
            value={toIdStrings(form.values[field.key])}
            onChange={(v) => form.setFieldValue(field.key, v)}
          />
        );
      case 'pairs':
        return (
          <PairsInput
            label={label}
            value={toPairs(form.values[field.key])}
            onChange={(v) => form.setFieldValue(field.key, v)}
            maxItems={field.maxItems}
          />
        );
      default:
        return null;
    }
  }

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(dropBlankPairs(fields, values)))}>
      <Stack gap="md">
        {fields.map((field) => (
          <Box key={field.key}>{renderField(field)}</Box>
        ))}
        <Flex gap="sm" mt="md">
          <Button type="submit" loading={isSubmitting}>Save</Button>
          <Button type="button" variant="default" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
        </Flex>
      </Stack>
    </form>
  );
}
